/**
 * A small toast stack.
 *
 * It exists for one job: a rejected command. `applyCommand` is total — an
 * invalid payload or a missing document comes back as a typed `CommandError`
 * with the state unchanged (see the commands README) — so the UI needs somewhere
 * to say so without a dialog and without pretending the edit landed.
 *
 * Hand-rolled rather than pulled in: two variants, one animation, no queueing
 * policy, no promise API. A dependency would be larger than the component.
 */

import { AlertTriangleIcon, CheckCircle2Icon, XIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { createContext, use, useCallback, useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

export type ToastVariant = 'default' | 'error';

export interface ToastOptions {
  readonly title: string;
  readonly description?: string;
  readonly variant?: ToastVariant;
  /** Milliseconds before it dismisses itself. Errors linger longer. */
  readonly durationMs?: number;
  /**
   * One inline action, e.g. "Undo".
   *
   * Deliberately singular and deliberately optional. A destructive action that
   * is instantly reversible does not need a confirmation dialog in front of it
   * — it needs an undo behind it, and the undo has to be *where the user is
   * looking* when it happens. Selecting it dismisses the toast.
   */
  readonly action?: { readonly label: string; readonly onClick: () => void };
}

interface ToastRecord extends ToastOptions {
  readonly id: number;
}

interface ToastContextValue {
  readonly toast: (options: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const DEFAULT_DURATION_MS = 4000;
const ERROR_DURATION_MS = 7000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<readonly ToastRecord[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((item) => item.id !== id));
  }, []);

  const toast = useCallback(
    (options: ToastOptions) => {
      const id = nextId.current++;
      setToasts((current) => [...current.slice(-3), { ...options, id }]);
      const duration =
        options.durationMs ??
        (options.variant === 'error' ? ERROR_DURATION_MS : DEFAULT_DURATION_MS);
      setTimeout(() => dismiss(id), duration);
    },
    [dismiss],
  );

  const value = useMemo<ToastContextValue>(() => ({ toast }), [toast]);

  return (
    <ToastContext value={value}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed right-4 bottom-4 z-100 flex w-full max-w-sm flex-col gap-2"
      >
        {toasts.map((item) => (
          <output
            key={item.id}
            className={cn(
              'pointer-events-auto flex items-start gap-2.5 rounded-lg border bg-popover p-3 text-popover-foreground shadow-lg',
              'animate-in slide-in-from-bottom-2 fade-in-0',
              item.variant === 'error' && 'border-destructive/40',
            )}
          >
            {item.variant === 'error' ? (
              <AlertTriangleIcon className="mt-0.5 size-4 shrink-0 text-destructive" />
            ) : (
              <CheckCircle2Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
            )}
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">{item.title}</p>
              {item.description !== undefined && (
                <p className="mt-0.5 text-xs break-words text-muted-foreground">
                  {item.description}
                </p>
              )}
              {item.action !== undefined && (
                <button
                  type="button"
                  onClick={() => {
                    item.action?.onClick();
                    dismiss(item.id);
                  }}
                  className="mt-1.5 rounded-sm text-xs font-medium underline underline-offset-4 transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                >
                  {item.action.label}
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => dismiss(item.id)}
              aria-label="Dismiss"
              className="rounded-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
            >
              <XIcon className="size-3.5" />
            </button>
          </output>
        ))}
      </div>
    </ToastContext>
  );
}

export function useToast(): ToastContextValue {
  const context = use(ToastContext);
  if (context === null) throw new Error('useToast must be used inside a <ToastProvider>');
  return context;
}
